/**
 * Locale and Regional Settings Configuration
 * 
 * This file contains all country-specific settings for the application.
 * Currently configured for Brazil (pt-BR).
 * 
 * Optimized to use native Intl APIs for better performance.
 */

import { getRegionConfig } from "./region-config";

export const LOCALE_CONFIG = {
  // Locale and Language
  locale: "pt-BR",
  language: "pt",
  country: "BR",
  countryName: "Brasil",

  // Currency Settings
  currency: {
    code: "BRL",
    symbol: "R$",
    symbolPosition: "before", // "before" or "after"
    name: "Real Brasileiro",
    decimalPlaces: 2,
  },

  // Number Formatting
  number: {
    decimalSeparator: ",",
    thousandsSeparator: ".",
    groupSize: 3,
  },

  // Date Formatting
  date: {
    format: "DD/MM/YYYY", // Display format
    shortFormat: "DD/MM/YY",
    longFormat: "DD [de] MMMM [de] YYYY",
    weekStart: 0, // 0 = Sunday, 1 = Monday
    firstWeekOfYear: 1, // ISO week numbering
  },

  // Time Formatting
  time: {
    format: "HH:mm", // 24-hour format
    formatWithSeconds: "HH:mm:ss",
    use24Hour: true,
  },

  // Phone Number
  phone: {
    format: "(XX) XXXXX-XXXX", // Mobile
    landlineFormat: "(XX) XXXX-XXXX",
    countryCode: "+55",
    maskMobile: "(99) 99999-9999",
    maskLandline: "(99) 9999-9999",
  },

  // Postal Code
  postalCode: {
    format: "XXXXX-XXX", // CEP format
    mask: "99999-999",
    name: "CEP",
  },

  // Address Format
  address: {
    order: ["street", "number", "complement", "neighborhood", "city", "state", "postalCode"],
    stateType: "state", // "state" or "province"
    labels: {
      street: "Rua",
      number: "Número",
      complement: "Complemento",
      neighborhood: "Bairro",
      city: "Cidade",
      state: "Estado",
      postalCode: "CEP",
    },
  },

  // Measurement Units
  units: {
    distance: "km", // kilometers
    distanceShort: "m", // meters
    weight: "kg", // kilograms
    weightSmall: "g", // grams
    temperature: "°C", // Celsius
    area: "m²",
    volume: "L", // liters
  },

  // Timezone
  timezone: "America/Sao_Paulo",
  timezoneOffset: "-03:00", // BRT (Brasília Time)

  // Business Settings (fallback; use getFiscalIdFormat() for platform-driven masks)
  business: {
    taxName: "Imposto",
    taxRate: 0, // Default, can vary by category
    fiscalIdName: "CPF/CNPJ",
    fiscalIdFormat: {
      cpf: "999.999.999-99",
      cnpj: "99.999.999/9999-99",
    },
  },

  // Days of Week (for display)
  daysOfWeek: {
    full: ["Domingo", "Segunda-feira", "Terça-feira", "Quarta-feira", "Quinta-feira", "Sexta-feira", "Sábado"],
    short: ["Dom", "Seg", "Ter", "Qua", "Qui", "Sex", "Sáb"],
    min: ["D", "S", "T", "Q", "Q", "S", "S"],
  },

  // Months (for display)
  months: {
    full: [
      "Janeiro", "Fevereiro", "Março", "Abril", "Maio", "Junho",
      "Julho", "Agosto", "Setembro", "Outubro", "Novembro", "Dezembro"
    ],
    short: ["Jan", "Fev", "Mar", "Abr", "Mai", "Jun", "Jul", "Ago", "Set", "Out", "Nov", "Dez"],
  },

  // Payment Methods (common in Brazil)
  paymentMethods: {
    creditCard: "Cartão de Crédito",
    debitCard: "Cartão de Débito",
    pix: "PIX",
    bankSlip: "Boleto Bancário",
    cash: "Dinheiro",
  },
} as const;

/**
 * Returns fiscal ID format (cpf/cnpj masks) from config-platform when available, otherwise locale fallback.
 * Use for input placeholders, maxLength, and any UI that should reflect platform config.
 */
export function getFiscalIdFormat(): { cpf: string; cnpj: string } {
  const fromPlatform = getRegionConfig()?.business?.fiscalIdFormat;
  const fallback = LOCALE_CONFIG.business.fiscalIdFormat;
  return {
    cpf: fromPlatform?.cpf ?? fallback.cpf,
    cnpj: fromPlatform?.cnpj ?? fallback.cnpj,
  };
}

/** Placeholder for CPF input: derived from fiscalIdFormat.cpf (9 → 0), e.g. "000.000.000-00". No separate config. */
export function getCpfPlaceholder(): string {
  return getFiscalIdFormat().cpf.replace(/9/g, "0");
}

/** Max length for CPF input: derived from fiscalIdFormat.cpf length (e.g. 14). No separate config. */
export function getCpfMaskLength(): number {
  return getFiscalIdFormat().cpf.length;
}

// Memoized formatters using native Intl API for better performance
const currencyFormatter = new Intl.NumberFormat(LOCALE_CONFIG.locale, {
  style: "currency",
  currency: LOCALE_CONFIG.currency.code,
  minimumFractionDigits: LOCALE_CONFIG.currency.decimalPlaces,
  maximumFractionDigits: LOCALE_CONFIG.currency.decimalPlaces,
});

const numberFormatter = new Intl.NumberFormat(LOCALE_CONFIG.locale, {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

const dateFormatter = new Intl.DateTimeFormat(LOCALE_CONFIG.locale, {
  day: "2-digit",
  month: "2-digit",
  year: "numeric",
});

const dateFormatterShort = new Intl.DateTimeFormat(LOCALE_CONFIG.locale, {
  day: "2-digit",
  month: "2-digit",
  year: "2-digit",
});

const dateFormatterLong = new Intl.DateTimeFormat(LOCALE_CONFIG.locale, {
  day: "2-digit",
  month: "long",
  year: "numeric",
});

const timeFormatter = new Intl.DateTimeFormat(LOCALE_CONFIG.locale, {
  hour: "2-digit",
  minute: "2-digit",
  hour12: false,
});

const timeFormatterWithSeconds = new Intl.DateTimeFormat(LOCALE_CONFIG.locale, {
  hour: "2-digit",
  minute: "2-digit",
  second: "2-digit",
  hour12: false,
});

/**
 * Format currency according to locale settings
 * Uses native Intl.NumberFormat for optimal performance.
 * Coerces to number so objects never cause React #300 when result is rendered.
 */
export function formatCurrency(amount: number): string {
  const n = Number(amount);
  return Number.isFinite(n) ? currencyFormatter.format(n) : "0";
}

/** pt-BR number with 2 decimals, no currency symbol (for price inputs). */
const brlPriceInputFormatter = new Intl.NumberFormat(LOCALE_CONFIG.locale, {
  minimumFractionDigits: LOCALE_CONFIG.currency.decimalPlaces,
  maximumFractionDigits: LOCALE_CONFIG.currency.decimalPlaces,
});

/**
 * Format centavos digit string as Brazilian price display (e.g. "900000" → "9.000,00").
 * Each typed digit appends to the right (last 2 digits are centavos), common POS pattern in BR.
 */
export function formatBrlPriceInputFromDigits(digits: string): string {
  const clean = digits.replace(/\D/g, "");
  if (!clean) return "";
  const cents = parseInt(clean, 10);
  if (!Number.isFinite(cents)) return "";
  return brlPriceInputFormatter.format(cents / 100);
}

/** Reais → digit string for mask (e.g. 9000 → "900000"). */
export function reaisToBrlPriceDigits(reais: number): string {
  const n = Number(reais);
  if (!Number.isFinite(n) || n < 0) return "";
  return String(Math.round(n * 100));
}

/** Initial / display string from a numeric price in reais. */
export function formatBrlPriceInputFromReais(reais: number): string {
  return formatBrlPriceInputFromDigits(reaisToBrlPriceDigits(reais));
}

/**
 * Parse masked or raw input to reais (strips non-digits, divides by 100).
 * Works for "9.000,00", "900000", etc.
 */
export function parseBrlPriceInput(s: string): number {
  const clean = String(s ?? "").replace(/\D/g, "");
  if (!clean) return NaN;
  const cents = parseInt(clean, 10);
  if (!Number.isFinite(cents)) return NaN;
  return cents / 100;
}

/** Use in onChange: keep only digits, reformat display. */
export function maskBrlPriceInputChange(raw: string): string {
  const digits = raw.replace(/\D/g, "");
  if (!digits) return "";
  return formatBrlPriceInputFromDigits(digits);
}

/**
 * Format number according to locale settings
 * Uses native Intl.NumberFormat for optimal performance
 */
export function formatNumber(num: number, decimalPlaces: number = 2): string {
  const formatter = decimalPlaces === 2
    ? numberFormatter
    : new Intl.NumberFormat(LOCALE_CONFIG.locale, {
        minimumFractionDigits: decimalPlaces,
        maximumFractionDigits: decimalPlaces,
      });
  
  return formatter.format(num);
}

/**
 * Format a date-only string (YYYY-MM-DD) as DD/MM/YYYY without timezone shift.
 * ISO date-only strings are parsed as UTC midnight, which in negative-UTC locales (e.g. Brazil)
 * would display as the previous calendar day. This helper avoids that by not using Date for formatting.
 * Use for birth_date and other date-only values from the API.
 */
export function formatDateOnly(isoDateString: string): string {
  const match = typeof isoDateString === "string" && isoDateString.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (match) {
    const [, y, m, d] = match;
    return `${d}/${m}/${y}`;
  }
  return formatDate(isoDateString);
}

/**
 * Format date according to locale settings
 * Uses native Intl.DateTimeFormat for optimal performance
 * @param date - Date object or ISO string
 * @param format - "short" | "long" | "default"
 */
export function formatDate(date: Date | string, format: "short" | "long" | "default" = "default"): string {
  const dateObj = typeof date === "string" ? new Date(date) : date;
  
  if (format === "short") {
    return dateFormatterShort.format(dateObj);
  } else if (format === "long") {
    return dateFormatterLong.format(dateObj);
  } else {
    return dateFormatter.format(dateObj);
  }
}

/**
 * Format time according to locale settings
 * Uses native Intl.DateTimeFormat for optimal performance
 */
export function formatTime(date: Date | string, includeSeconds: boolean = false): string {
  const dateObj = typeof date === "string" ? new Date(date) : date;
  
  return includeSeconds 
    ? timeFormatterWithSeconds.format(dateObj)
    : timeFormatter.format(dateObj);
}

/**
 * Format phone number according to locale settings
 * Optimized with early returns
 */
export function formatPhoneNumber(phone: string): string {
  // Remove all non-numeric characters
  const cleaned = phone.replace(/\D/g, "");
  
  // Check if it's a mobile (11 digits) or landline (10 digits)
  if (cleaned.length === 11) {
    // Mobile: (XX) XXXXX-XXXX
    return cleaned.replace(/(\d{2})(\d{5})(\d{4})/, "($1) $2-$3");
  }
  
  if (cleaned.length === 10) {
    // Landline: (XX) XXXX-XXXX
    return cleaned.replace(/(\d{2})(\d{4})(\d{4})/, "($1) $2-$3");
  }
  
  return phone; // Return original if format doesn't match
}

/**
 * Format CEP (postal code) according to locale settings
 * Optimized with early return
 */
export function formatCEP(cep: string): string {
  // Remove all non-numeric characters
  const cleaned = cep.replace(/\D/g, "");
  
  if (cleaned.length === 8) {
    // CEP format: XXXXX-XXX
    return cleaned.replace(/(\d{5})(\d{3})/, "$1-$2");
  }
  
  return cep; // Return original if format doesn't match
}

/**
 * Format CPF (individual tax ID)
 * Optimized with early return
 */
export function formatCPF(cpf: string): string {
  const cleaned = cpf.replace(/\D/g, "");
  
  if (cleaned.length === 11) {
    return cleaned.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, "$1.$2.$3-$4");
  }
  
  return cpf;
}

/**
 * Format CNPJ (company tax ID)
 * Optimized with early return
 */
export function formatCNPJ(cnpj: string): string {
  const cleaned = cnpj.replace(/\D/g, "");
  
  if (cleaned.length === 14) {
    return cleaned.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, "$1.$2.$3/$4-$5");
  }
  
  return cnpj;
}

// Cache for relative time calculations
const MINUTE_MS = 60 * 1000;
const HOUR_MS = 60 * MINUTE_MS;
const DAY_MS = 24 * HOUR_MS;

/**
 * Get relative time string (e.g., "Hoje", "Ontem", "2 dias atrás")
 * Optimized with constants and early returns
 */
export function getRelativeTime(date: Date | string): string {
  const dateObj = typeof date === "string" ? new Date(date) : date;
  const diffMs = Date.now() - dateObj.getTime();

  if (diffMs < MINUTE_MS) return "Agora";
  if (diffMs < HOUR_MS) {
    const minutes = Math.floor(diffMs / MINUTE_MS);
    return `${minutes} minuto${minutes > 1 ? "s" : ""} atrás`;
  }
  if (diffMs < DAY_MS) {
    const hours = Math.floor(diffMs / HOUR_MS);
    return `${hours} hora${hours > 1 ? "s" : ""} atrás`;
  }
  
  const days = Math.floor(diffMs / DAY_MS);
  
  if (days === 0) return "Hoje";
  if (days === 1) return "Ontem";
  if (days < 7) return `${days} dias atrás`;
  if (days < 30) {
    const weeks = Math.floor(days / 7);
    return `${weeks} semana${weeks > 1 ? "s" : ""} atrás`;
  }
  if (days < 365) {
    const months = Math.floor(days / 30);
    return `${months} ${months > 1 ? "meses" : "mês"} atrás`;
  }
  
  const years = Math.floor(days / 365);
  return `${years} ano${years > 1 ? "s" : ""} atrás`;
}

/**
 * Validate CPF (Brazilian individual tax ID)
 * Uses the official CPF validation algorithm
 */
export function validateCPF(cpf: string): boolean {
  const cleaned = cpf.replace(/\D/g, "");
  
  if (cleaned.length !== 11) return false;
  
  // Check if all digits are the same
  if (/^(\d)\1{10}$/.test(cleaned)) return false;
  
  // Validate first check digit
  let sum = 0;
  for (let i = 0; i < 9; i++) {
    sum += parseInt(cleaned.charAt(i)) * (10 - i);
  }
  let checkDigit = 11 - (sum % 11);
  if (checkDigit >= 10) checkDigit = 0;
  if (checkDigit !== parseInt(cleaned.charAt(9))) return false;
  
  // Validate second check digit
  sum = 0;
  for (let i = 0; i < 10; i++) {
    sum += parseInt(cleaned.charAt(i)) * (11 - i);
  }
  checkDigit = 11 - (sum % 11);
  if (checkDigit >= 10) checkDigit = 0;
  if (checkDigit !== parseInt(cleaned.charAt(10))) return false;
  
  return true;
}

/**
 * Validate Brazilian phone number
 */
export function validatePhone(phone: string): boolean {
  const cleaned = phone.replace(/\D/g, "");
  
  // Must be 10 digits (landline) or 11 digits (mobile)
  if (cleaned.length !== 10 && cleaned.length !== 11) return false;
  
  // Area code must be between 11 and 99
  const areaCode = parseInt(cleaned.substring(0, 2));
  if (areaCode < 11 || areaCode > 99) return false;
  
  // Mobile numbers start with 9
  if (cleaned.length === 11 && cleaned.charAt(2) !== "9") return false;
  
  return true;
}

/**
 * Validate CEP (Brazilian postal code)
 */
export function validateCEP(cep: string): boolean {
  const cleaned = cep.replace(/\D/g, "");
  return cleaned.length === 8;
}

/**
 * Validate email address
 */
export function validateEmail(email: string): boolean {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
}